import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Loader2, RefreshCw, ToggleLeft, ToggleRight,
  Activity, AlertTriangle, Search, Filter, ChevronDown, ChevronUp, Power
} from 'lucide-react';
import { apiClient } from '../api/client';
import { toast } from '../utils/toastUtils';

const DynamicOrchestratorTools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, enabled: 0, disabled: 0, installed: 0 });
  const [togglingTool, setTogglingTool] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedPhases, setExpandedPhases] = useState({});

  // Phase display names
  const phaseNames = {
    RECONNAISSANCE: 'Reconnaissance',
    ENUMERATION: 'Énumération',
    VULNERABILITY_SCANNING: 'Scan de Vulnérabilités',
    EXPLOITATION: 'Exploitation',
    POST_EXPLOITATION: 'Post-Exploitation',
    REPORTING: 'Rapport'
  };

  // Phase colors
  const phaseColors = {
    RECONNAISSANCE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    ENUMERATION: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    VULNERABILITY_SCANNING: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    EXPLOITATION: 'bg-red-500/20 text-red-400 border-red-500/30',
    POST_EXPLOITATION: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    REPORTING: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  // Fetch tools from settings API
  const fetchTools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/settings/tools');
      const data = response.data;

      setTools(data.tools || []);
      setStats({
        total: data.total || 0,
        enabled: data.enabled_count || 0,
        disabled: data.disabled_count || 0,
        installed: data.installed_count || 0
      });

      // Initialize all phases as expanded
      const phases = {};
      (data.tools || []).forEach(t => {
        if (t.phase && !phases[t.phase]) {
          phases[t.phase] = true;
        }
      });
      setExpandedPhases(phases);

    } catch (err) {
      console.error('Error fetching tools:', err);
      setError('Erreur lors du chargement des outils');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  // Toggle tool enabled/disabled
  const toggleTool = async (toolName, currentEnabled) => {
    try {
      setTogglingTool(toolName);

      const endpoint = currentEnabled
        ? `/settings/tools/${toolName}/disable`
        : `/settings/tools/${toolName}/enable`;

      await apiClient.post(endpoint);

      // Update local state
      setTools(prev => prev.map(t =>
        t.name === toolName ? { ...t, enabled: !currentEnabled } : t
      ));

      // Update stats
      setStats(prev => ({
        ...prev,
        enabled: currentEnabled ? prev.enabled - 1 : prev.enabled + 1,
        disabled: currentEnabled ? prev.disabled + 1 : prev.disabled - 1
      }));

      toast.success(`${toolName} ${currentEnabled ? 'desactive' : 'active'}`);

    } catch (err) {
      console.error('Error toggling tool:', err);
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setTogglingTool(null);
    }
  };

  // Bulk enable/disable all tools
  const bulkToggle = async (enable) => {
    try {
      setTogglingTool('bulk');

      await apiClient.post('/settings/tools/bulk', {
        tools: tools.filter(t => t.installed).map(t => t.name),
        enabled: enable
      });

      // Refresh tools
      await fetchTools();

      toast.success(`Tous les outils ${enable ? 'actives' : 'desactives'}`);

    } catch (err) {
      console.error('Error bulk toggling tools:', err);
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setTogglingTool(null);
    }
  };

  // Toggle phase expansion
  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }));
  };

  // Filter tools
  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPhase = filterPhase === 'all' || tool.phase === filterPhase;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'up' && tool.system_status === 'up') ||
      (filterStatus === 'down' && tool.system_status === 'down') ||
      (filterStatus === 'enabled' && tool.enabled) ||
      (filterStatus === 'disabled' && !tool.enabled);

    return matchesSearch && matchesPhase && matchesStatus;
  });

  // Group by phase
  const toolsByPhase = {};
  filteredTools.forEach(tool => {
    const phase = tool.phase || 'OTHER';
    if (!toolsByPhase[phase]) {
      toolsByPhase[phase] = [];
    }
    toolsByPhase[phase].push(tool);
  });

  // Get unique phases for filter
  const uniquePhases = [...new Set(tools.map(t => t.phase))].filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">Chargement des outils...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={fetchTools}
          className="btn-green px-4 py-2 flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-green">{stats.installed}</div>
              <div className="text-xs text-gray-400">Installes</div>
            </div>
            <div className="h-8 w-px bg-gray-700"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{stats.enabled}</div>
              <div className="text-xs text-gray-400">Actives</div>
            </div>
            <div className="h-8 w-px bg-gray-700"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-400">{stats.disabled}</div>
              <div className="text-xs text-gray-400">Desactives</div>
            </div>
            <div className="h-8 w-px bg-gray-700"></div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">{stats.total}</div>
              <div className="text-xs text-gray-400">Total</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => bulkToggle(true)}
              disabled={togglingTool === 'bulk'}
              className="flex items-center gap-2 px-3 py-2 bg-brand-green/20 hover:bg-brand-green/30 text-brand-green rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              title="Activer tous les outils"
            >
              {togglingTool === 'bulk' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ToggleRight className="w-4 h-4" />
              )}
              Tout activer
            </button>
            <button
              onClick={() => bulkToggle(false)}
              disabled={togglingTool === 'bulk'}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              title="Desactiver tous les outils"
            >
              {togglingTool === 'bulk' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
              Tout desactiver
            </button>
            <div className="h-8 w-px bg-gray-700 mx-1"></div>
            <button
              onClick={fetchTools}
              className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
              title="Rafraichir"
            >
              <RefreshCw className="w-5 h-5 text-gray-400 hover:text-brand-green" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher un outil..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-black-lighter border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
            />
          </div>

          {/* Phase Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              className="bg-brand-black-lighter border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-brand-green focus:outline-none"
            >
              <option value="all">Toutes les phases</option>
              {uniquePhases.map(phase => (
                <option key={phase} value={phase}>{phaseNames[phase] || phase}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-brand-black-lighter border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-brand-green focus:outline-none"
          >
            <option value="all">Tous les statuts</option>
            <option value="up">Système UP</option>
            <option value="down">Système DOWN</option>
            <option value="enabled">Activé</option>
            <option value="disabled">Désactivé</option>
          </select>
        </div>
      </div>

      {/* Tools by Phase */}
      {Object.entries(toolsByPhase).map(([phase, phaseTools]) => (
        <div key={phase} className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
          {/* Phase Header */}
          <button
            onClick={() => togglePhase(phase)}
            className={`w-full flex items-center justify-between p-4 hover:bg-brand-black-lighter transition-colors ${phaseColors[phase] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
          >
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5" />
              <span className="font-bold">{phaseNames[phase] || phase}</span>
              <span className="text-xs opacity-75">
                ({phaseTools.filter(t => t.system_status === 'up').length}/{phaseTools.length} UP)
              </span>
            </div>
            {expandedPhases[phase] ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {/* Phase Tools */}
          {expandedPhases[phase] && (
            <div className="divide-y divide-gray-800">
              {phaseTools.map((tool) => (
                <div
                  key={tool.name}
                  className={`p-4 flex items-center justify-between gap-4 transition-colors ${
                    tool.system_status === 'up'
                      ? 'hover:bg-brand-black-lighter'
                      : 'bg-gray-900/50 opacity-75'
                  }`}
                >
                  {/* Tool Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Tool Name */}
                      <span className="font-semibold text-white">{tool.name}</span>

                      {/* System Status Badge */}
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${
                        tool.system_status === 'up'
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}>
                        {tool.system_status === 'up' ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        Système {tool.system_status === 'up' ? 'UP' : 'DOWN'}
                      </span>

                      {/* Enabled Status Badge */}
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${
                        tool.enabled
                          ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                          : 'bg-gray-600/20 text-gray-400 border-gray-600/30'
                      }`}>
                        <Power className="w-3 h-3" />
                        {tool.enabled ? 'Activé' : 'Désactivé'}
                      </span>

                      {/* Priority Badge */}
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-700/50 text-gray-400">
                        Priorité: {tool.priority}
                      </span>
                    </div>

                    {/* Resources */}
                    <div className="mt-1 text-xs text-gray-500 flex items-center gap-3">
                      <span>CPU: {(tool.cpu_weight * 100).toFixed(0)}%</span>
                      <span>RAM: {tool.ram_gb}GB</span>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <div className="flex items-center gap-3">
                    {tool.system_status === 'down' && (
                      <span className="flex items-center gap-1 text-xs text-yellow-400">
                        <AlertTriangle className="w-4 h-4" />
                        Non installé
                      </span>
                    )}
                    <button
                      onClick={() => toggleTool(tool.name, tool.enabled)}
                      disabled={togglingTool === tool.name || tool.system_status === 'down'}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        tool.enabled ? 'bg-brand-green' : 'bg-gray-600'
                      } ${(togglingTool === tool.name || tool.system_status === 'down') ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={tool.system_status === 'down' ? 'Outil non installé' : (tool.enabled ? 'Désactiver' : 'Activer')}
                    >
                      {togglingTool === tool.name ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin mx-auto" />
                      ) : (
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            tool.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Empty State */}
      {filteredTools.length === 0 && (
        <div className="bg-brand-black rounded-lg p-8 border border-gray-800 text-center">
          <p className="text-gray-400">Aucun outil ne correspond aux filtres</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-brand-green mt-0.5">&#128161;</div>
          <div className="text-sm text-gray-300">
            <div className="font-medium text-brand-green mb-1">Dynamic Orchestrator Tools</div>
            <div>
              Cette section affiche les outils du Dynamic Orchestrator utilisés pour les scans automatisés.
              <br/>
              <strong>Système UP/DOWN</strong>: Indique si l'outil est installé sur le système.
              <br/>
              <strong>Activé/Désactivé</strong>: Permet d'activer ou désactiver l'utilisation de l'outil dans les scans.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicOrchestratorTools;
